import { useMemo } from "react";

import { NO_COLUMN_LABEL } from "./taskBoardActivity";
import { useActivityEntries } from "./useActivityEntries";
import {
  buildActivityColorScale,
  COLUMN_PALETTE,
  STAFF_PALETTE,
} from "../../../taskboard/components/activityChartStyle";

/**
 * アクティビティ集計ビューと**同じ**配色をタスクボード側でも使うためのフック。
 *
 * 配色の割り当てはアクティビティ集計と同一のロジック（全期間の実績データから
 * キーを名前順に並べ、パレットを順に配る）で作るため、集計ビューで見た
 * 「山田さんの色」「MA 列の色」がタスクボードでも同じ色になる。
 *
 * 実績（`@山田[3h]` 記法）が 1 件も無い名前・列は色を持たない（undefined）。
 * 呼び出し側は undefined のとき既定の色（チップの青・チェックボックスのグレー等）
 * のままにする。
 */
export interface ActivityColorScales {
  /** `@表示名` → 色。実績が無い名前は undefined（既定色のまま）。 */
  // eslint-disable-next-line no-unused-vars
  staffColorOf: (name: string) => string | undefined;
  /** 列（工程）名 → 色。実績が無い列は undefined。 */
  // eslint-disable-next-line no-unused-vars
  columnColorOf: (columnTitle: string) => string | undefined;
}

export function useActivityColors(): ActivityColorScales {
  // 全プロジェクト・全ボード・全タスクを走査して平坦化したアクティビティ
  // （ActivityMonitorView と同じ元データ。配色は全期間のデータから作る）。
  const { entries } = useActivityEntries();

  const staffColorOf = useMemo(() => {
    const staff = new Set<string>();
    for (const e of entries) staff.add(e.name);
    const scale = buildActivityColorScale([...staff].sort(), STAFF_PALETTE);
    return (name: string) => (staff.has(name) ? scale(name) : undefined);
  }, [entries]);

  const columnColorOf = useMemo(() => {
    const columns = new Set<string>();
    for (const e of entries) columns.add(e.columnTitle || NO_COLUMN_LABEL);
    const scale = buildActivityColorScale([...columns].sort(), COLUMN_PALETTE);
    return (columnTitle: string) =>
      columns.has(columnTitle) ? scale(columnTitle) : undefined;
  }, [entries]);

  return { staffColorOf, columnColorOf };
}
